import {
  CONTENT_TYPE,
  IVariationContainer,
  IVariationContainerFields,
} from 'marketing-site/@types/generated/contentful'
import { renderTransformedComponent } from 'marketing-site/lib/transformedComponents'
import { useAssignedVariations } from 'marketing-site/src/components/context/AssignedVariationsContext'
import EditingContext from 'marketing-site/src/components/context/EditingContext'
import React, { useContext, useState } from 'react'
import optimizelyIcon from '../../images/optimizely.png'
import { Entry } from 'contentful'

export function isVariationContainer(entry: Entry<any>): entry is IVariationContainer {
  return entry.sys.contentType?.sys.id === 'variationContainer'
}

function getVisibleVariationEntryId(
  meta: IVariationContainerFields['meta'],
  visibleVariationKey: string,
): string | null {
  if (!meta) return null

  const availableVariations = Object.keys(meta)
  if (availableVariations.includes(visibleVariationKey)) {
    return meta[visibleVariationKey]
  } else if (availableVariations.includes('control')) {
    return meta['control']
  } else if (availableVariations.includes('off')) {
    return meta['off']
  }

  return null
}

export function ContentfulOptimizelyVariationContainer(
  variationContainer: IVariationContainer & { children?: React.ReactNode },
): React.ReactElement | null {
  const { experimentKey, variations, meta } = variationContainer.fields
  const variationIds = Object.keys(meta || {})

  const showEditorUI = useContext(EditingContext)
  const assignments = useAssignedVariations()
  const assignedVariation = assignments[experimentKey || '']
  const [visibleVariationKey, setVisibleVariationKey] = useState(assignedVariation?.variationKey)

  if (!experimentKey || !variations || !meta) {
    // TODO: error handling
    // The Contentful entry was unpublished or malformed in some way
    return null
  }

  const visibleVariationEntryId = getVisibleVariationEntryId(meta, visibleVariationKey)
  if (!visibleVariationEntryId) {
    // TODO: error handling
    // Optimizely assigned us to a variation that doesn't have a mapping in Contentful
    return null
  }

  const visibleVariationEntry = variations.find((v) => v.sys.id === visibleVariationEntryId)
  if (!visibleVariationEntry || !visibleVariationEntry.fields) {
    // TODO: error handling
    // Optimizely assigned us to a variation that _does_ have a mapping in Contentful, but the
    // mapping points to an unpublished or nonexistent entry.
    return null
  }

  const transformedComponent = (
    <>
      {renderTransformedComponent(visibleVariationEntry.sys.contentType?.sys.id as CONTENT_TYPE, {
        ...visibleVariationEntry,
        children: variationContainer.children,
      })}
    </>
  )

  if (showEditorUI) {
    return (
      <div className="ab-test-switcher">
        <form className="ab-test-switcher__form">
          <p className="ab-test-switcher__experiment-id">{experimentKey}</p>
          {variationIds.map((variationId) => (
            <div className="ab-test-switcher__form__field" key={variationId}>
              <input
                type="radio"
                name="shownVariant"
                onChange={(event) => setVisibleVariationKey(event.target.value)}
                value={variationId}
                id={`${experimentKey}-${variationId}`}
                checked={visibleVariationKey === variationId}
              />

              <label htmlFor={`${experimentKey}-${variationId}`}>{variationId}</label>
            </div>
          ))}
        </form>
        {transformedComponent}
        <style jsx>{`
          .ab-test-switcher {
            position: relative;

            $self: &;

            &:after,
            &__form {
              z-index: 999;
              position: absolute;
              top: 0;
              left: 10px;
              right: 10px;
            }

            &:after {
              content: '';
              display: block;
              bottom: 0;
              border: 4px solid blue;
              border-radius: 5px;
              pointer-events: none;
            }

            &__form {
              background: transparent url('${optimizelyIcon.src}');
              background-position: top 10px left 10px;
              background-repeat: no-repeat;
              background-size: 25px;
              padding-left: 45px;
              font-family: monospace;

              display: flex;
              align-items: baseline;
            }

            &__experiment-id {
              color: blue;
              font-weight: bold;
            }

            &__form__field {
              margin-left: 20px;

              label {
                padding: 15px 10px;
                display: inline-block;
              }
            }

            &:after,
            &__form__field {
              transition: all 200ms ease;
              opacity: 0;
            }

            &:hover {
              #{$self}__form__field,
              &:after {
                opacity: 1;
              }
            }
          }
        `}</style>
      </div>
    )
  }

  return transformedComponent
}
